import DataObjectIcon from '@mui/icons-material/DataObject';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Box, Divider, MenuItem, Select, Stack } from '@mui/material';
import { type ElementType, type ReactNode, useState } from 'react';

import { type RequireLabelOrAria, colors, isDefined } from 'common';

import { BlockValueTemplateInnerField } from './FlowTemplate';
import { useReactFlowViewContext } from '../../../context/context';
import { useSelectedNodeField } from '../useSelectedNodeField';

export type BlockFieldMode = 'Base' | 'BlockValueTemplate';

const BlockFieldContentByMode: Record<BlockFieldMode, { Icon: ElementType; content: string }> = {
  Base: { Icon: FormatListBulletedIcon, content: 'Select' },
  BlockValueTemplate: { Icon: DataObjectIcon, content: 'Block Value' },
};

type BlockValueTemplateFieldWrapperProps = {
  fieldId: string;
  fieldName: string;
  children: ReactNode;
  onModeChange?: (mode: BlockFieldMode) => void;
} & RequireLabelOrAria;

export function BlockValueTemplateFieldWrapper(props: BlockValueTemplateFieldWrapperProps) {
  const { fieldId, fieldName, children, label, 'aria-label': ariaLabel, onModeChange } = props;
  const { field, visibleError } = useSelectedNodeField<any>(fieldName);
  const { isEditable } = useReactFlowViewContext();

  const [fieldMode, setFieldMode] = useState<BlockFieldMode>(
    typeof field.value === 'string' && field.value?.includes('{{') ? 'BlockValueTemplate' : 'Base'
  );
  return (
    <Stack
      direction="row"
      width="100%"
      sx={{
        border: '1px solid',
        borderColor: isDefined(visibleError) ? colors.error.default : colors.border.default,
        borderRadius: 1,
        paddingRight: 1,
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      divider={<Divider orientation="vertical" flexItem />}
    >
      <Box>
        <Select<BlockFieldMode>
          disableUnderline
          variant="standard"
          onChange={(event) => {
            setFieldMode(event.target.value);
            onModeChange?.(event.target.value);
          }}
          value={fieldMode}
          sx={{
            paddingX: 0,
          }}
          disabled={!isEditable}
          renderValue={(selectedValue) => {
            const Icon = BlockFieldContentByMode[selectedValue].Icon;
            return (
              <Icon
                fontSize="small"
                sx={{
                  verticalAlign: 'text-bottom',
                }}
              />
            );
          }}
        >
          {Object.entries(BlockFieldContentByMode).map(([value, { Icon, content }]) => (
            <MenuItem
              key={value}
              value={value}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Icon fontSize="small" /> {content}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box flex={1} maxWidth="calc(100% - 52px)">
        {fieldMode === 'Base' ? children : null}
        {fieldMode === 'BlockValueTemplate' ? (
          <BlockValueTemplateInnerField
            fieldId={fieldId}
            fieldName={fieldName}
            aria-label={typeof label === 'string' ? label : (ariaLabel ?? 'Value')}
            variant="borderless"
          />
        ) : null}
      </Box>
    </Stack>
  );
}
